import React, {useCallback, useMemo} from "react";
import {BackOrdersListsOrdersDataGridCellKeys, InvoicesListsOrdersDataGridCellKeys} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes} from "../../../../../../../../../packages/data-grid";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {KeyboardArrowRight, OpenInNew} from "@mui/icons-material";
import Button from "../../../../../../../../components/base/button/button";
import PartnerDataGrid, {useDataGridSavedState} from "../../../../../../../../containers/data-grid";
import {CircularProgress, SvgIcon} from "@mui/material";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import {ReactComponent as Printer} from '../../../../../../../../../assets/images/printer.svg'


const BackOrdersListViewOrdersTable = ({
                                           searchData,
                                           exportToCsv,
                                           data,
                                           dataGridSaveConfig,
                                           fetchingOrdersCustomer,
                                           navigateToInformationView,
                                           navigateToInvoiceView,
                                           navigateToPrintView,
                                           revertToQuote,
                                       }) => {
    const operatorAccessLevels = useAccessLevels();

    /**
     * Opens the confirmation-dialog to ensure that the user wants to revert the selected order.
     * @type {function(order: any): void}
     */
    const getUserConfirmation = useCallback((order) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    By reverting this order to a quote, all of its committed quantities and selected serial numbers will
                    be removed. Do you want to proceed?
                </p>
            </div>,
            {},
            (decision) => decision && revertToQuote(order.customer, order?.id),
        );
    }, [revertToQuote])

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.sales.salesOrderInformation ? [
                {
                    name: BackOrdersListsOrdersDataGridCellKeys.navigate,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: true,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    width: 0,
                },
                ...(operatorAccessLevels.packages.bizPrint.printSalesOrder ? [
                    {
                        name: BackOrdersListsOrdersDataGridCellKeys.print,
                        alignment: DataGridColumnAlignments.center,
                        type: DataGridColumnTypes.element,
                        pinned: true,
                        sortable: false,
                        pinnedToggleable: false,
                        resizable: false,
                        visibilityToggleable: false,
                        width: 0,
                    },
                ] : []),
            ] : []),
            {
                title: "Order No",
                name: BackOrdersListsOrdersDataGridCellKeys.orderNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: true,
            },
            {
                title: "Customer",
                name: BackOrdersListsOrdersDataGridCellKeys.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Connected Invoice No",
                name: BackOrdersListsOrdersDataGridCellKeys.invoiceNo,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 200
            },
            {
                title: "Order Date",
                name: BackOrdersListsOrdersDataGridCellKeys.orderDate,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
            },
            {
                title: "Customer PO",
                name: BackOrdersListsOrdersDataGridCellKeys.customerPO,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Sales Person",
                name: BackOrdersListsOrdersDataGridCellKeys.salespersonNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Total Ordered",
                name: BackOrdersListsOrdersDataGridCellKeys.totalOrdered,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 120,
            },
            ...(operatorAccessLevels.packages.sales.manageSalesOrders ? [
                {
                    name: BackOrdersListsOrdersDataGridCellKeys.revertToQuote,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: true,
                    pinnedType: DataGridColumnPinnedTypes.right,
                    pinnedToggleable: false,
                    sortable: false,
                    reorderable: false,
                    visibilityToggleable: false,
                    width: 200,
                },
            ] : []),
        ], [operatorAccessLevels.packages.bizPrint.printSalesOrder, operatorAccessLevels.packages.sales.manageSalesOrders, operatorAccessLevels.packages.sales.salesOrderInformation])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return data?.map(order => ({
            key: order.id,
            data: order,
            cells: {
                [InvoicesListsOrdersDataGridCellKeys.navigate]:
                    <IconButton
                        color={'secondary'}
                        disabled={fetchingOrdersCustomer.includes(order.id)}
                        onClick={() => navigateToInformationView(order.customer, order.id)}

                    >
                        {
                            fetchingOrdersCustomer.includes(order.id)
                                ? <CircularProgress size={20} color={'secondary'}/>
                                : <OpenInNew className='icon normal'/>
                        }
                    </IconButton>,
                [InvoicesListsOrdersDataGridCellKeys.print]:
                    <IconButton
                        color={'paper.secondary'}
                        disabled={fetchingOrdersCustomer.includes(order.id)}
                        onClick={() => navigateToPrintView(order.customer, order.id)}
                    >
                        {
                            fetchingOrdersCustomer.includes(order.id)
                                ? <CircularProgress size={20} color={'secondary'}/>
                                : <SvgIcon className='icon medium' component={Printer}/>
                        }

                    </IconButton>,
                [BackOrdersListsOrdersDataGridCellKeys.orderNo]: order.orderNo,
                [BackOrdersListsOrdersDataGridCellKeys.customer]: order.customer?.name,
                [BackOrdersListsOrdersDataGridCellKeys.invoiceNo]:
                    !order.backOrderRelatedInvoiceInfo?.invoiceNo
                        ? null
                        : <Button
                            className={'px-0 d-flex justify-content-center align-items-center'}
                            variant={'text'}
                            color={'primary'}
                            onClick={() => operatorAccessLevels.packages.sales.invoiceInformation && navigateToInvoiceView(order.customer, order.id, order.backOrderRelatedInvoiceInfo?.invoiceId)}
                        >
                            <p>{order.backOrderRelatedInvoiceInfo?.invoiceNo ?? ""}</p>
                            <KeyboardArrowRight/>
                        </Button>,
                [BackOrdersListsOrdersDataGridCellKeys.orderDate]: order.orderDate,
                [BackOrdersListsOrdersDataGridCellKeys.customerPO]: order.customerPO,
                [BackOrdersListsOrdersDataGridCellKeys.salespersonNo]: order.salespersonNo,
                [BackOrdersListsOrdersDataGridCellKeys.totalOrdered]: order.total,
                [BackOrdersListsOrdersDataGridCellKeys.revertToQuote]: order?.isClosed
                    ? null
                    : <Button
                        variant={'outlined'}
                        color={'primary'}
                        disabled={order.loading || fetchingOrdersCustomer.includes(order.id)}
                        onClick={() => getUserConfirmation(order)}
                    >
                        {
                            order.loading
                                ? 'loading...'
                                : "Revert to Quote"
                        }
                    </Button>
            }
        }))
    }, [data, fetchingOrdersCustomer, getUserConfirmation, navigateToInformationView, navigateToInvoiceView, navigateToPrintView, operatorAccessLevels.packages.sales.invoiceInformation])

    return (
        <>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={dataGridRows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                getExportInfo={exportToCsv}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
        </>
    );
}

export default BackOrdersListViewOrdersTable;
